import React, { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import { SYSTEM_GROUPING_OPTIONS as systemGroupingOptions } from '../../config';
import { useCompleteParameterOptions } from '../../hooks/useCompleteParameterOptions';
import { filterTextParameterOptions } from '../../lib/revit/parameterOptionTypeFilter';
import { workflowSetupTooltips } from '../../config/wizardTooltips';
import CompactFieldRow from '../workflow/CompactFieldRow';
import { layoutGap } from '../../../theme/theme';
import {
  ActiveSectionHeader,
  EditorSurface,
  WorkflowNotice,
} from '../workflow/WorkflowStepComponents';

type Option = 'Iso Drawings Generation' | 'Pressure Drop Calculations' | 'Stress Calculations' | null;

interface ContentAreaProps {
  selectedOption: Option;
  objectTypeValue: string;
  sKeyValue: string;
  pipingSpecValue: string;
  systemGroupingValue: string;
  defaultPropertiesConfirmed: boolean;
  onObjectTypeChange: (value: string) => void;
  onSKeyChange: (value: string) => void;
  onPipingSpecChange: (value: string) => void;
  onSystemGroupingChange: (value: string) => void;
  onDefaultPropertiesChange: (checked: boolean) => void;
  onContinueClick: () => void;
  hideCheckboxAndButton?: boolean;
  showValidationErrors?: boolean;
  validationErrors?: Record<string, string>;
}

const ContentArea: React.FC<ContentAreaProps> = ({
  selectedOption,
  objectTypeValue,
  sKeyValue,
  pipingSpecValue,
  systemGroupingValue,
  onObjectTypeChange,
  onSKeyChange,
  onPipingSpecChange,
  onSystemGroupingChange,
  showValidationErrors = false,
  validationErrors = {},
}) => {
  const { options: parameterOptions } = useCompleteParameterOptions();
  // OBJECT-TYPE / SKEY / PIPING-SPEC all hold text values, so only text-typed
  // parameters are offered; unknown-typed options stay visible (parameterOptionTypeFilter).
  const textParameterOptions = useMemo(
    () => filterTextParameterOptions(parameterOptions),
    [parameterOptions]
  );

  // Helper to get field error
  const getFieldError = (field: string): string | undefined => {
    if (showValidationErrors) {
      return validationErrors[field];
    }
    return undefined;
  };

  // Show content area for all three options
  if (selectedOption === 'Iso Drawings Generation' || selectedOption === 'Pressure Drop Calculations' || selectedOption === 'Stress Calculations') {
    const requiredFieldSpecs = [
      {
        id: 'objectTypeValue',
        label: 'OBJECT-TYPE *',
        value: objectTypeValue,
        onChange: onObjectTypeChange,
        help: workflowSetupTooltips.objectType,
        options: textParameterOptions,
      },
      {
        id: 'sKeyValue',
        label: 'SKEY *',
        value: sKeyValue,
        onChange: onSKeyChange,
        help: workflowSetupTooltips.sKey,
        options: textParameterOptions,
      },
      ...(selectedOption === 'Iso Drawings Generation'
        ? [{
            id: 'pipingSpecValue',
            label: 'PIPING-SPEC *',
            value: pipingSpecValue,
            onChange: onPipingSpecChange,
            help: workflowSetupTooltips.pipingSpec,
            options: textParameterOptions,
          }]
        : []),
      {
        id: 'systemGroupingValue',
        label: 'System grouping *',
        value: systemGroupingValue,
        onChange: onSystemGroupingChange,
        help: workflowSetupTooltips.systemGrouping,
        options: systemGroupingOptions,
        allowCustomValue: false,
      },
    ];

    const requiredFieldCount = requiredFieldSpecs.length;
    const completedFieldCount = requiredFieldSpecs.filter(
      (field) => field.value.trim() && !getFieldError(field.id)
    ).length;
    const missingFieldCount = requiredFieldCount - completedFieldCount;

    return (
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: layoutGap.row,
        }}
      >
        <EditorSurface sx={{ p: 1, minWidth: 0 }}>
          <ActiveSectionHeader
            title="Required PCF defaults"
            description="Map each PCF key to its Revit source parameter. PipeEX uses these mappings during connector setup and saves them in the mapping profile."
            statusLabel={
              missingFieldCount === 0
                ? `Mapped ${completedFieldCount}/${requiredFieldCount}`
                : `${missingFieldCount} mapping${missingFieldCount === 1 ? '' : 's'} missing`
            }
            statusTone={missingFieldCount === 0 ? 'set' : 'missing'}
          />

          {parameterOptions.length === 0 && (
            <WorkflowNotice severity="info" sx={{ mt: 0.75 }}>
              No shared parameter definitions are loaded. Enter an exact Revit parameter name, or load
              a shared parameter file to search its definitions.
            </WorkflowNotice>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: layoutGap.row,
              mt: layoutGap.row,
            }}
          >
            {requiredFieldSpecs.map((field) => (
              <CompactFieldRow
                key={field.id}
                label={field.label}
                value={field.value}
                onChange={field.onChange}
                options={field.options}
                allowCustomValue={field.allowCustomValue}
                help={field.help}
                error={getFieldError(field.id)}
                required
              />
            ))}
          </Box>
        </EditorSurface>
      </Box>
    );
  }

  return null;
};

export default memo(ContentArea);
